import { useQuery } from "@tanstack/react-query";

import { useConfig } from "../useConfig";
import useToken from "../useToken";
import type { CartItem } from "./formatters";
import type { CentraAddress, CentraTotals, ISelectionSummary } from "./types";

export type UseSelectionReturn = Awaited<ReturnType<typeof getSelection>>;
export const getSelection = async (
  token: string,
  setToken: (token: string) => void,
  api?: string
) => {
  if (!api) console.error("No API URL provided");
  const res = await fetch(`${api}/selection/`, {
    headers: { "API-token": token || "" },
  });

  const data = await res.json();

  if (data === null) throw Error("Error getting selection");
  setToken(data.token);

  return data;
};

type Language = {
  language: string;
  name: string;
  default: boolean;
};

export type CountryType = {
  changeLocation: (stateId?: string) => void;
  code: string;
  currency: string;
  default: boolean;
  country: string;
  language: string;
  name: string;
  pricelist: string;
  states?: {
    state: string;
    name: string;
  }[];
};

export type Selection = {
  countries: CountryType[];
  languages: Language[];
  selection: {
    items: CartItem[];
    summary: ISelectionSummary;
    currency: string;
    totals: CentraTotals;
    address: CentraAddress;
  };
  location: {
    country: string;
    name: string;
    pricelist: string;
    state: string;
  };
};

//type TokenType = string;

const useSelection = () => {
  const { config } = useConfig();
  const { token, setToken } = useToken();

  const api = config?.centra.url;

  const selection = useQuery<Selection>(
    ["giftcard-selection", token, api],
    () => getSelection(token, setToken, api)
  );

  return {
    data: selection.data,
    items: selection.data?.selection.items as CartItem[],
    summary: selection.data?.selection.summary as ISelectionSummary,
    isLoading: selection.isLoading,
    location: selection.data?.location,
    refetch: selection.refetch,
    priceList: selection?.data?.location?.pricelist as string,
    address: selection?.data?.selection?.address,
  };
};

export default useSelection;
